import { InfoItem, CertificateStatus, CopyToClipboard } from '@/components'
import { taxRegimeLabelMap } from '@/constants'
import { useCompany } from '@/context/companies'
import {
  formatCNPJ,
  formatDate,
  isChangingAccountantWithoutCertificate,
} from '@/helpers'
import {
  Button,
  Card,
  Typography,
  Stack,
  Divider,
  useMediaQuery,
  Theme,
  Box,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { CertificateUploadDialog } from './certificate-upload-dialog'

export function CompanyData() {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'))

  const { company } = useCompany()
  const [isCertificateDialogOpen, setIsCertificateDialogOpen] = useState(false)

  const {
    key,
    corporate_name,
    tax_id,
    municipal_registration,
    tax_regime,
    certificates,
  } = company

  const canRegisterCertificate =
    isChangingAccountantWithoutCertificate(company)

  const copyValue = useMemo(() => {
    const displayValue = []
    if (corporate_name) displayValue.push(`Razão Social: ${corporate_name}`)
    if (tax_id) displayValue.push(`CNPJ: ${formatCNPJ(tax_id)}`)
    if (municipal_registration)
      displayValue.push(`Inscrição Municipal: ${municipal_registration}`)
    if (tax_regime)
      displayValue.push(
        `Regime tributário: ${taxRegimeLabelMap[tax_regime.tax_regime]}`,
      )
    return displayValue.join('\n')
  }, [corporate_name, tax_id, municipal_registration, tax_regime])

  return (
    <Card>
      <Stack direction="row" alignItems="center" mb={2}>
        <Typography fontSize={18}>Dados da empresa</Typography>
        <CopyToClipboard value={copyValue} variant="copy-all" />
      </Stack>

      <Stack direction={isMobile ? 'column' : 'row'} gap={isMobile ? 1 : 3}>
        <Box
          width={{
            sm: '100%',
            md: '70%',
          }}
        >
          <InfoItem
            label="CNPJ"
            value={tax_id ? formatCNPJ(tax_id) : ''}
            enableCopy
          />
          <InfoItem label="Razão Social" value={corporate_name} enableCopy />
          <InfoItem
            label="Inscrição Municipal"
            value={municipal_registration}
          />
          {tax_regime ? (
            <InfoItem
              label="Regime tributário"
              value={taxRegimeLabelMap[tax_regime.tax_regime]}
            />
          ) : null}
        </Box>

        <Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />

        <Box
          width={{
            sm: '100%',
            md: '30%',
          }}
        >
          <InfoItem
            label="Certificado digital"
            value={
              certificates?.length ? (
                <CertificateStatus
                  expirationDate={certificates[0].expiration_date}
                />
              ) : (
                'Não possui certificado digital'
              )
            }
          />

          {certificates?.length ? (
            <Typography>
              Válido até {formatDate(certificates[0].expiration_date)}
            </Typography>
          ) : null}

          {canRegisterCertificate ? (
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
              onClick={() => setIsCertificateDialogOpen(true)}
            >
              Cadastrar certificado
            </Button>
          ) : null}
        </Box>
      </Stack>

      <CertificateUploadDialog
        open={isCertificateDialogOpen}
        companyKey={key}
        onClose={() => setIsCertificateDialogOpen(false)}
      />
    </Card>
  )
}
